import { Amplify } from 'aws-amplify';
import { getCurrentUser } from 'aws-amplify/auth';
import React, { useEffect, useState } from 'react';

import QboConnect from './angie/quickbooks/QboConnect.tsx';

import PipedriveConnect from './angie/pipedrive/PipedriveConnect.tsx';
import PipedriveDisconnect from './angie/pipedrive/PipedriveDisconnect.tsx';

import QboLaunch from './angie/quickbooks/QboLaunch.tsx';
import QboDisconnect from './angie/quickbooks/QboDisconnect.tsx';

import '@aws-amplify/ui-react/styles.css';
import authConfig from '../configs/authConfig';

import '../App.css';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import Copyright from '../Copyright.js';
import EditProfile from '../EditProfile.js';
import Home from '../Home.js';
import Tos from '../routes/tos.js';
import PrivacyPolicy from '../routes/PrivacyPolicy.js';
import UsersDelete from '../routes/users-delete.js';
import OrderPFAS from '../OrderPFAS';
import Messages from './Messages';
import SupportContent from '../support/SupportContent';
import ReportDetails from '../reports/ReportDetails';
import HowToUnderstandPfasCorePanelResult from '../reports/HowToUnderstandPfasCorePanelResult';
import Profile from '../Profile';
import UserLogin from '../components/user/UserLogin';
import OrdersList from '../shop/OrdersList';
import OrderDetailsPage from '../shop/OrderDetailsPage.tsx';
import GuestOrderDetails from '../shop/GuestOrderDetails';
import MainUserRegistration from '../components/user/MainUserRegistration';
import ForgotPassword from '../components/user/ForgotPassword';
import ResetPasswordConfirmation from '../components/user/ResetPasswordConfirmation';
import NotFoundPage from '../components/common/NotFoundPage.tsx';
import { useAuthenticator } from '@aws-amplify/ui-react';
import ProtectedRoute from '../components/layouts/ProtectedRoute';
import MainTestKitActivation from '../components/test_kits/MainTestKitActivation';
import LayoutV2 from '../components/layouts/LayoutV2';
import GradientCircularProgressContainer from '../components/common/GradientCircularProgressContainer';
import { AUTHENTICATED } from '../constants/user';
import UserRegistrationLayout from '../components/layouts/UserRegistrationLayout';
import { DXE_IS_MOBILE_APP } from '../constants/localStorageKeys';
import SessionInactivityWarningModal from '../components/user/SessionActivityWarningModal';
import TestResultsHistory from '../reports/TestResultsHistory';
import HowToCollectSample from '../components/pages/howTo/HowToCollectSample';
import { BaseThemeContextProvider } from '../contexts/BaseThemeContextProvider';
import ProfileThemeToggle from '../components/user/ProfileThemeToggle';
import FrostHome from '../frost/FrostHome';
import AppointmentsPage from '../frost/AppointmentsPage';
import RecordsPage from '../frost/RecordsPage';
import UserHipaaNoticeAcknowledgement from '../frost/UserHipaaNoticeAcknowledgement';
import ClientDownloadEmailTemplatePage from '../frost/ClientDownloadEmailTemplate';
import Organizations from './organizations/Organizations';
import Organization from './organizations/Organization';
import Accounts from './organizations/accounts/Accounts';
import Account from './organizations/accounts/Account';
import AccountStaff from './organizations/accounts/account/AccountStaff';
import AccountEvents from './organizations/accounts/account/events/AccountEvents';
import AccountEvent from './organizations/accounts/account/events/AccountEvent';
import CreateEventPage from './organizations/accounts/account/events/CreateEventPage';
import EditEventPage from './organizations/accounts/account/events/EditEventPage';
import EventFormQrCodePage from './organizations/accounts/account/events/EventFormQrCodePage';
import AccountLocations from './organizations/accounts/account/locations/AccountLocations';
import AccountLocation from './organizations/accounts/account/locations/AccountLocation';
import UnauthorizedPage from '../components/common/Unauthorized';
import OrganizationStaff from './organizations/OrganizationStaff';
import EditOrganization from './organizations/EditOrganization';
import OrdersListOrg from './organizations/orders/OrdersListOrg';
import OrderDetailsOrg from './organizations/orders/OrderDetailsOrg';
import AccountPatientDetails from './organizations/accounts/account/AccountPatientDetails';
import QuestionnaireAnswers from '../frost/QuestionnaireAnswers';

const app_co = process.env.REACT_APP_COMPANY;

Amplify.configure(authConfig);

const renderUserRegistrationLayout = (Component, layoutProps = {}, componentProps = {}) => {
  return (
    <UserRegistrationLayout {...layoutProps}>
      <Component {...componentProps} />
    </UserRegistrationLayout>
  );
};

const renderLayoutV2With = (Component, layoutProps = {}, componentProps = {}) => {
  return (
    <LayoutV2 {...layoutProps}>
      <Component {...componentProps} />
    </LayoutV2>
  );
};

const renderLayoutV2WithSessionExpirationModal = (Component, layoutProps = {}, componentProps = {}) => {
  return (
    <LayoutV2 {...layoutProps}>
      <SessionInactivityWarningModal />
      <Component {...componentProps} />
    </LayoutV2>
  );
};

function renderProtectedV2Layout(Component, componentProps = {}, layoutProps = {}) {
  return <>{renderLayoutV2With(ProtectedRoute, layoutProps, { element: <Component {...componentProps} /> })}</>;
}

function getFrostSpecificRoutes(currentUser) {
  if (app_co === 'frost') {
    return (
      <>
        {/* Unauthenticated pages */}
        <Route path="signup" element={renderUserRegistrationLayout(MainUserRegistration)} />
        <Route
          path="/acknowledge-hipaa"
          element={renderLayoutV2With(UserHipaaNoticeAcknowledgement, {
            navigateTo: '/',
            pageTitle: 'HIPAA Acknowledgment',
            activeLink: 'User',
            activeLinkText: '',
          })}
        />
        <Route path="/download-email-template.html" element={<ClientDownloadEmailTemplatePage />} />
        <Route
          path="/"
          element={renderLayoutV2WithSessionExpirationModal(FrostHome, {
            activeLinkText: '',
            navigateTo: '/',
            pageTitle: 'Frost Home',
            showHeaderOnMobile: true,
            showLoginOption: true,
          })}
        />
        <Route
          path="/appointments.html"
          element={renderProtectedV2Layout(
            AppointmentsPage,
            {},
            {
              activeLinkText: 'Appointments',
              navigateTo: '/',
              pageTitle: 'Appointments',
            },
          )}
        />
        <Route
          path="/records.html"
          element={renderProtectedV2Layout(
            RecordsPage,
            {},
            {
              activeLinkText: 'Records',
              navigateTo: '/',
              pageTitle: 'Records',
            },
          )}
        />
        {/* Authenticated pages */}
        <Route path="/o/" element={renderProtectedV2Layout(Organizations, {}, {})} />
        <Route path="/o/:org_path_name" element={renderProtectedV2Layout(Organization, {}, {})} />
        <Route path="/o/:org_path_name/edit" element={renderProtectedV2Layout(EditOrganization, {}, {})} />
        <Route path="/o/:org_path_name/orders" element={renderProtectedV2Layout(OrdersListOrg, {}, {})} />
        <Route path="/o/:org_path_name/orders/:order_id" element={renderProtectedV2Layout(OrderDetailsOrg, {}, {})} />
        <Route path="/o/:org_path_name/a" element={renderProtectedV2Layout(Accounts, {}, {})} />
        <Route path="/o/:org_path_name/a/:acc_path_name" element={renderProtectedV2Layout(Account, {}, {})} />
        <Route
          path="/o/:org_path_name/a/:acc_path_name/staff"
          element={renderProtectedV2Layout(AccountStaff, {}, {})}
        />
        <Route
          path="/o/:org_path_name/a/:acc_path_name/events"
          element={renderProtectedV2Layout(AccountEvents, {}, {})}
        />
        <Route
          path="/o/:org_path_name/a/:acc_path_name/events/new"
          element={renderProtectedV2Layout(CreateEventPage, {}, {})}
        />
        <Route
          path="/o/:org_path_name/a/:acc_path_name/events/:event_id/edit"
          element={renderProtectedV2Layout(EditEventPage, {}, {})}
        />
        <Route
          path="/o/:org_path_name/a/:acc_path_name/events/:event_id/forms/:form_id/qr-code"
          element={renderProtectedV2Layout(
            EventFormQrCodePage,
            { scope: 'event' },
            { pageTitle: 'Event Form QR Code' },
          )}
        />
        <Route
          path="/o/:org_path_name/a/:acc_path_name/events/:event_id/guests/:guest_id/requirements/:requirement_id/qr-code"
          element={renderProtectedV2Layout(
            EventFormQrCodePage,
            { scope: 'attendee' },
            { pageTitle: 'Employee Form QR Code' },
          )}
        />
        <Route
          path="/o/:org_path_name/a/:acc_path_name/events/:event_id"
          element={renderProtectedV2Layout(AccountEvent, {}, {})}
        />
        <Route
          path="/o/:org_path_name/a/:acc_path_name/p/:patient_user_id"
          element={renderProtectedV2Layout(AccountPatientDetails, {}, {})}
        />
        <Route
          path="/o/:org_path_name/a/:acc_path_name/p/:patient_user_id/q/:submission_id"
          element={renderProtectedV2Layout(QuestionnaireAnswers, {}, {})}
        />
        <Route
          path="/o/:org_path_name/a/:acc_path_name/locations"
          element={renderProtectedV2Layout(AccountLocations, {}, {})}
        />
        <Route
          path="/o/:org_path_name/a/:acc_path_name/locations/:location_id"
          element={renderProtectedV2Layout(AccountLocation, {}, {})}
        />
        <Route path="/o/:org_path_name/staff" element={renderProtectedV2Layout(OrganizationStaff, {}, {})} />
      </>
    );
  } else {
    return <></>;
  }
}

function getRhSpecificRoutes(currentUser) {
  if (app_co === 'rh') {
    return (
      <>
        {/* Unauthenticated pages */}
        <Route
          path="/"
          element={renderLayoutV2WithSessionExpirationModal(
            Home,
            {
              activeLinkText: '',
              showLoginOption: true,
            },
            {
              user: currentUser,
            },
          )}
        />
        <Route
          path="guest-activate.html"
          element={renderUserRegistrationLayout(MainTestKitActivation, {
            activeLinkText: '',
          })}
        />
        <Route path="signup" element={renderUserRegistrationLayout(MainUserRegistration)} />
        {/* Authenticated pages */}
        <Route
          path="/results.html"
          element={renderProtectedV2Layout(
            TestResultsHistory,
            {},
            {
              activeLinkText: 'Results',
              navigateTo: '/',
              pageTitle: 'Results',
              user: currentUser,
            },
          )}
        />
        <Route
          path="orders.html"
          element={renderProtectedV2Layout(
            OrdersList,
            {},
            {
              activeLinkText: 'Orders',
              navigateTo: '/',
              toolTipHelpId: 'track-order-bar',
              pageTitle: 'Orders',
              activeLink: 'Orders',
              user: currentUser,
            },
          )}
        />
        <Route
          path="/report/:id"
          element={renderProtectedV2Layout(
            ReportDetails,
            {},
            {
              activeLinkText: 'Results',
              navigateTo: '/',
              pageTitle: 'Results',
              user: currentUser,
            },
          )}
        />
        <Route
          path="/pfas-info.html"
          element={renderProtectedV2Layout(
            HowToUnderstandPfasCorePanelResult,
            {},
            {
              activeLinkText: 'Results',
              navigateTo: '/',
              pageTitle: 'PFAS Information',
              user: currentUser,
            },
          )}
        />
        <Route
          path="order.html"
          element={renderLayoutV2WithSessionExpirationModal(OrderPFAS, {
            activeLinkText: '',
          })}
        />
        <Route
          path="orders/:shopifyOrderId"
          element={renderLayoutV2WithSessionExpirationModal(OrderDetailsPage, {
            activeLinkText: 'Orders',
            navigateTo: '/orders.html',
            toolTipHelpId: 'track-order-bar',
            pageTitle: 'Track Order',
            showHeaderOnMobile: true,
            activeLink: 'Orders',
          })}
        />
        <Route
          path="guest-orders/:shopifyOrderId"
          element={renderLayoutV2With(GuestOrderDetails, {
            activeLinkText: 'Orders',
            navigateTo: '/',
            toolTipHelpId: 'track-order-bar',
            pageTitle: 'Orders',
            activeLink: 'Orders',
          })}
        />
        <Route
          path="how-to-collect-sample.html"
          element={renderProtectedV2Layout(HowToCollectSample, {
            activeLinkText: '',
          })}
        />
        <Route
          path="activate.html"
          element={renderLayoutV2WithSessionExpirationModal(MainTestKitActivation, {
            activeLinkText: '',
            showLoginOption: false,
          })}
        />
      </>
    );
  } else {
    return <></>;
  }
}

function App() {
  const { authStatus, signOut } = useAuthenticator((context) => [context.authStatus, context.signOut]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleCalendarRedirect = (location, navigate) => {
    const params = new URLSearchParams(location.search);
    const calendarStatus = params.get('calendar');
    if (!calendarStatus) {
      return false;
    }

    if (calendarStatus === 'error') {
      localStorage.setItem('calendar_connect_error', params.get('reason') || 'error');
    } else {
      localStorage.removeItem('calendar_connect_error');
    }

    const returnTo = localStorage.getItem('calendar_connect_return_to');
    if (returnTo) {
      localStorage.removeItem('calendar_connect_return_to');
      if (returnTo !== `${location.pathname}${location.search}${location.hash}`) {
        navigate(returnTo, { replace: true });
        return true;
      }
    }

    params.delete('calendar');
    params.delete('reason');
    const nextSearch = params.toString();
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${location.hash ?? ''}`;
    navigate(nextUrl, { replace: true });
    return true;
  };

  useEffect(() => {
    if (authStatus !== 'configuring') {
      setIsLoading(false);
    }

    const getUserData = async () => {
      try {
        const currentUser = await getCurrentUser();
        setCurrentUser(currentUser);
      } catch (error) {
        setCurrentUser(null);
      }
    };

    if (authStatus === AUTHENTICATED) {
      getUserData();
    } else {
      setCurrentUser(null);
    }
  }, [authStatus]);

  useEffect(() => {
    handleCalendarRedirect(location, navigate);
  }, [location, navigate]);

  if (isLoading) {
    return <GradientCircularProgressContainer />;
  }

  return (
    <BaseThemeContextProvider user={currentUser}>
      <Routes>
        {/* Angie QBO Routes */}
        <Route
          path="/angie/quickbooks/connect"
          element={renderProtectedV2Layout(QboConnect, {}, { pageTitle: 'Connect QuickBooks' })}
        />
        <Route
          path="/angie/quickbooks/launch"
          element={renderProtectedV2Layout(QboLaunch, {}, { pageTitle: 'QuickBooks' })}
        />
        <Route
          path="/angie/quickbooks/disconnect"
          element={renderProtectedV2Layout(QboDisconnect, {}, { pageTitle: 'Disconnect QuickBooks' })}
        />

        {/* Angie Pipedrive Routes */}
        <Route
          path="/angie/pipedrive/connect"
          element={renderProtectedV2Layout(PipedriveConnect, {}, { pageTitle: 'Connect Pipedrive' })}
        />
        <Route
          path="/angie/pipedrive/disconnect"
          element={renderProtectedV2Layout(PipedriveDisconnect, {}, { pageTitle: 'Disconnect Pipedrive' })}
        />

        {/* Protected routes need the user */}
        <Route
          path="/profile.html"
          element={renderProtectedV2Layout(
            Profile,
            {},
            {
              activeLinkText: 'Profile',
              navigateTo: '/',
              pageTitle: 'Profile',
            },
          )}
        />
        <Route
          path="users/delete.html"
          element={renderProtectedV2Layout(
            UsersDelete,
            {},
            {
              user: currentUser,
              signOut: signOut,
            },
          )}
        />
        <Route path="users/edit.html" element={renderProtectedV2Layout(EditProfile, {}, {})} />
        <Route
          path="/inbox.html"
          element={renderProtectedV2Layout(
            Messages,
            {},
            {
              activeLinkText: 'Profile',
              navigateTo: '/',
              pageTitle: 'Profile',
              user: currentUser,
            },
          )}
        />
        <Route
          path="/appearance.html"
          element={renderProtectedV2Layout(
            ProfileThemeToggle,
            {},
            {
              activeLinkText: 'Profile',
              navigateTo: '/',
              pageTitle: 'Profile',
            },
          )}
        />
        {/* Unauthenticated pages */}
        <Route
          path="tos.html"
          element={renderLayoutV2WithSessionExpirationModal(Tos, {
            activeLinkText: '',
            showLoginOption: true,
          })}
        />
        <Route
          path="privacy-policy.html"
          element={renderLayoutV2WithSessionExpirationModal(PrivacyPolicy, {
            activeLinkText: '',
            showLoginOption: true,
          })}
        />
        <Route
          path="/login"
          element={renderLayoutV2With(UserLogin, {
            showLoginOption: false,
            navigateTo: '/',
            pageTitle: 'User Login',
            activeLink: 'User',
            activeLinkText: '',
          })}
        />
        <Route
          path="/forgot-password"
          element={renderLayoutV2With(ForgotPassword, {
            navigateTo: '/',
            pageTitle: 'Forgot Password',
            activeLink: 'User',
            activeLinkText: '',
          })}
        />
        <Route
          path="/reset-password-confirmation"
          element={renderLayoutV2With(ResetPasswordConfirmation, {
            navigateTo: '/',
            pageTitle: 'Reset Password Confirmation',
            activeLink: 'User',
            activeLinkText: '',
          })}
        />
        <Route
          path="support.html"
          element={renderLayoutV2WithSessionExpirationModal(SupportContent, {
            activeLinkText: 'Support',
            navigateTo: '/',
            toolTipHelpId: 'support-bar',
            pageTitle: 'Support',
            showHeaderOnMobile: true,
            activeLink: 'Support',
          })}
        />
        {getRhSpecificRoutes(currentUser)}
        {getFrostSpecificRoutes(currentUser)}
        <Route
          path="/401.html"
          element={renderLayoutV2With(UnauthorizedPage, {
            activeLinkText: 'Unauthorized',
            navigateTo: '/',
          })}
        />
        <Route
          path="*"
          element={renderLayoutV2With(NotFoundPage, {
            activeLinkText: 'Not Found',
            navigateTo: '/',
          })}
        />
      </Routes>
      <Copyright />
    </BaseThemeContextProvider>
  );
}

// TODO: to start using this one from the
// utils/site.js instead and remove this one
// once that migration is completed
export const isMobileApp = localStorage.getItem(DXE_IS_MOBILE_APP);

export default App;
